import React, { useState } from 'react';
import { useModal } from 'react-modal-hook';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { AccordionSection, Button, Cell, H3, HeaderCell, HeaderRow, Label, ModalFactory, Row, Table, TableBody, TableHead } from 'components';
import { Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { DEFAULT_POOL_NAME, IPoolData, IPoolJob, ITipCalculatorResult } from 'types';
import { centsToDollarsDisplay, Duration, formatTime, toTitleCase } from 'utils';
import PoolModal from '../PoolModal/PoolModal';

const StyledAccordionSection = styled(AccordionSection)`
  .pool-item-content {
    background: ${theme.color.neutral[50]};
    padding: 0 ${toRem(16)};
  }

  .pool-item-table {
    margin-bottom: ${toRem(16)};
  }
`;

const HeaderItemWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${toRem(60)};
  width: 100%;

  h3 {
    font-size: ${toRem(16)};
  }

  label {
    font-size: ${toRem(12)};
    color: ${theme.color.neutral[500]};
  }
`;

const HeaderItem = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${toRem(6)};
`;

const SegmentTime = styled(Text)`
  color: ${theme.color.neutral[500]};
`;

const formatCalculatorResult = (result: ITipCalculatorResult): IPoolData => {
  const pools: IPoolJob[] = [];
  let totalMin = 0;
  let totalTip = 0;

  result.pools.forEach((pool) => {
    if (pool.received.length > 0) {
      const totalTips = Math.trunc(pool.received.reduce((sum, received) => sum + received.sources.reduce((sum, source) => sum + source.total, 0), 0));
      totalMin += pool.totals.totalMinWorked;
      totalTip += totalTips;
      pools.push({ jobTitle: pool.job.title, totalMinutes: pool.totals.totalMinWorked, totalTips });
    }
  });
  return { daySegment: result.segment, totalMinutes: totalMin, totalTips: totalTip, pools, result };
};

interface IPoolItem {
  calculatorResult: ITipCalculatorResult;
  businessDay?: string;
}

const PoolItem: React.FC<IPoolItem> = ({ calculatorResult, businessDay }) => {
  const [selectedJob, setSelectedJob] = useState<string>();
  const poolData = formatCalculatorResult(calculatorResult);

  const [showPoolReportModal, hidePoolModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hidePoolModal} isActive closeOnOutSideClick={false}>
        <PoolModal pool={poolData} jobTitle={selectedJob} businessDay={businessDay} onClose={hidePoolModal} />
      </ModalFactory>
    ),
    [selectedJob, poolData, businessDay]
  );

  const renderHeader = () => (
    <HeaderItemWrapper>
      <HeaderItem data-cy="header-total-time">
        <Label>Total Time</Label>
        <H3>{new Duration(poolData.totalMinutes).toString()}</H3>
      </HeaderItem>
      <HeaderItem data-cy="header-total-tips">
        <Label>Tips & Autograts</Label>
        <H3>{centsToDollarsDisplay(poolData.totalTips)}</H3>
      </HeaderItem>
    </HeaderItemWrapper>
  );

  const onAction = (jobTitle: string) => {
    setSelectedJob(jobTitle);
    showPoolReportModal();
  };

  return (
    <StyledAccordionSection
      className="pool-item"
      dataCy="pool-item-section"
      title={poolData.daySegment || dayjs(businessDay).format('MMMM Do, YYYY')}
      subtitle={
        poolData.daySegment !== DEFAULT_POOL_NAME &&
        poolData.result.start &&
        poolData.result.end && (
          <SegmentTime size="xsmall">
            {formatTime(poolData.result.start)} - {formatTime(poolData.result.end)}
          </SegmentTime>
        )
      }
      actions={renderHeader()}
      hideButton={poolData.pools.length === 0}
      open={false}
      compact
    >
      <Table data-cy="pool-item-table" className="pool-item-table">
        <TableHead>
          <HeaderRow>
            <HeaderCell data-cy="job-header">Job</HeaderCell>
            <HeaderCell data-cy="time-header">Time Worked</HeaderCell>
            <HeaderCell data-cy="amount-header">Tips & Autograts</HeaderCell>
            <HeaderCell />
          </HeaderRow>
        </TableHead>
        <TableBody>
          {poolData.pools.map((pool, idx) => (
            <Row key={idx} data-cy="table-row">
              <Cell wrapText data-label="jobTitle" minWidth={110}>
                <Text size="small" fontWeight="semibold">
                  {toTitleCase(pool.jobTitle)}
                </Text>
              </Cell>
              <Cell data-label="totalMinutes">
                <Text size="small">{new Duration(pool.totalMinutes).toString()}</Text>
              </Cell>
              <Cell data-label="totalTips">
                <Text size="small">{centsToDollarsDisplay(pool.totalTips)}</Text>
              </Cell>
              <Cell data-label="actionButton" align="right">
                <Button data-cy="pool-item-modal-link" appearance="secondary" size="small" onClick={() => onAction(pool.jobTitle)} label="Employees & Rules" />
              </Cell>
            </Row>
          ))}
        </TableBody>
      </Table>
    </StyledAccordionSection>
  );
};

export default PoolItem;
